import { useContext, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";

import MotionContext from "./MotionContext";
import prefersReducedMotion from "./prefers-reduced-motion";

// Nothing is hidden until the browser has said it is off screen.
//
// The obvious shape — render hidden, observe, unhide on intersection — paints
// the hidden state for at least one frame, because an observer's first callback
// arrives after the frame in which it was attached. On a cold load that frame is
// the entire opening screen. On a position restored by <ScrollRestoration> it is
// whatever the reader was already looking at, so they would watch the paragraph
// under their cursor fade in from nothing.
//
// Turning it around costs nothing. The element renders as itself, and the
// observer's first callback — which reports every target it was given,
// intersecting or not — is what writes the hidden state, onto the elements it
// has just certified are nowhere the reader can see.
//
// This is also why there is no `js-motion` class on <html> and no
// `@media (scripting: enabled)`: neither knows whether *this* observer attached,
// only whether the bundle might one day run. A chunk that fails to load or an
// effect that throws first would leave a page at opacity 0 with nothing left
// running to restore it.
const HIDDEN = "hidden";
const SHOWN = "shown";

export default function useRevealOnScroll(ref, attribute = "data-reveal") {
  const { enabled } = useContext(MotionContext);
  // The router's fragment rather than window.location.hash: under the memory
  // router the tests use, the browser's URL never moves at all, so a hook
  // reading the real location would never notice a jump.
  const { hash } = useLocation();

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // Whatever happens next, leave nothing behind. Every exit from this effect —
    // a bail-out below, a settings change, a jump to a fragment, an unmount —
    // puts the element back exactly as authored, because a half-applied reveal
    // is content stranded at opacity 0 with nothing still running to restore it.
    const reset = () => el.removeAttribute(attribute);

    // The fragment case is not politeness. fragment-landing.js decides the page
    // has stopped moving by comparing getBoundingClientRect().top across frames,
    // and that rect *includes* transforms — so a section mid-reveal reads to it
    // as a section still settling, and it lands on the anchor again for every
    // frame of the fade. A reader who asked for /index#contact wants to be at
    // the contact form, not to watch it arrive.
    if (
      !enabled ||
      prefersReducedMotion() ||
      hash ||
      typeof IntersectionObserver === "undefined"
    ) {
      reset();
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute(attribute, SHOWN);
            // Fires once and never replays: scrolling back up a page should not
            // re-run an entrance the reader has already been shown.
            observer.disconnect();
          } else if (!entry.target.hasAttribute(attribute)) {
            // Only the very first report may hide. Any later one would put an
            // element the reader has already seen back to nothing.
            entry.target.setAttribute(attribute, HIDDEN);
          }
        });
      },
      // No rootMargin. A negative bottom margin would report an element sitting
      // in the last stretch of the viewport as not-intersecting — and under the
      // ordering above, that report is precisely what hides it, so a card
      // already on screen would blink out and wait to be scrolled to. Trigger on
      // the first pixel; the fade finishes with the element well up the screen.
      { threshold: 0 }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      reset();
    };
  }, [ref, attribute, enabled, hash]);
}
